import { useEffect, useState, useMemo } from 'react';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
/**
 * A React hook that tracks the vertical scroll offset of an HTML element from the top of the document.
 *
 * This hook provides real-time tracking of an element's position by calculating its distance from
 * the top of the document (including scroll position). It automatically updates when:
 * - The element or any of its ancestors resize
 * - The window is resized
 * - The element's position changes due to layout shifts
 *
 * The hook uses ResizeObserver to efficiently monitor size changes and requestAnimationFrame
 * to throttle updates for optimal performance.
 *
 * @template T - The type of HTML element to track, extending HTMLElement
 *
 * @returns An object containing:
 * - `offset`: The current vertical offset in pixels from the top of the document
 * - `anchorRef`: A ref callback to attach to the element you want to track
 *
 * @example
 * ```tsx
 * function ScrollTracker() {
 *   const { offset, anchorRef } = useScrollOffset<HTMLDivElement>();
 *
 *   return (
 *     <div>
 *       <div ref={anchorRef}>Tracked element</div>
 *       <p>Current offset: {offset}px</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Using with a navigation component
 * function StickyNavigation() {
 *   const { offset, anchorRef } = useScrollOffset<HTMLElement>();
 *   const isSticky = offset < window.scrollY;
 *
 *   return (
 *     <nav ref={anchorRef} className={isSticky ? 'sticky' : ''}>
 *       Navigation content
 *     </nav>
 *   );
 * }
 * ```
 */
export const useScrollOffset = () => {
    const [offset, setOffset] = useState(0);
    const [node, setNode] = useState(null);
    const anchorRef = useCallbackRef(setNode);
    useEffect(() => {
        if (!node)
            return;
        let frameId = null;
        const updateOffset = () => {
            if (frameId != null)
                return;
            frameId = requestAnimationFrame(() => {
                const rect = node.getBoundingClientRect();
                const newOffset = rect.top + window.scrollY;
                setOffset(newOffset);
                frameId = null;
            });
        };
        // Observe the element itself and its ancestors
        const observers = [];
        let current = node;
        while (current) {
            const observer = new ResizeObserver(updateOffset);
            observer.observe(current);
            observers.push(observer);
            current = current.parentElement;
        }
        window.addEventListener('resize', updateOffset);
        updateOffset();
        return () => {
            observers.forEach((o) => o.disconnect());
            window.removeEventListener('resize', updateOffset);
            if (frameId)
                cancelAnimationFrame(frameId);
        };
    }, [node]);
    return useMemo(() => ({ offset, anchorRef }), [offset, anchorRef]);
};
